/**
 * What each phase-gate check is called, in one place.
 *
 * <h3>Why this is a module and not a switch</h3>
 *
 * Four surfaces kept their own map from the backend's check key to a sentence — two over the
 * Build gate's keys, two over the Decide and Act readiness keys — and each ended with
 * `default: return c.check`. So a check the backend added appeared correctly on none of them
 * and as a raw snake_case identifier on all of them, with nothing failing anywhere.
 *
 * Seen on the Build → Decide gate: three rows reading as sentences and a fourth reading
 * `protection_policy_eval_ready`, which is the gate's internal key and not something anybody
 * should be shown. One vocabulary — the backend's — so one map, and `phaseGateChecks.test.js`
 * reads the keys straight out of `PhaseGateService.java` so a new check cannot leak the same
 * way.
 *
 * The sentences say what the check is ABOUT. What it found, and what to do, come from the
 * backend's own `message`, which is written for the person reading it and should not be
 * paraphrased here.
 */

export const PHASE_GATE_CHECK_LABELS = {
  /* Build → Decide */
  registration_exists: 'External agent registration record exists',
  at_least_one_skill: 'Agent has at least 1 policy skill configured',
  at_least_one_eval_ready: 'At least one policy skill is eval-ready (completed eval + passing score)',
  protection_policy_eval_ready: 'Its protection policy is measured',
  skills_remaining_in_build: 'Policy skills remaining in Build (informational)',
  failed_evals_warning: 'Failed eval suites (informational)',

  /* Decide → Act */
  governance_attached: 'Protection attached',
  policy_enforceable: 'Protection is enforceable',
  autonomy_ceiling_set: 'Autonomy ceiling set',
  conformance_baseline_registered: 'Conformance baseline registered',
  at_least_one_reviewed_skill: 'At least one policy skill reviewed',
  autonomous_score_threshold: 'Autonomous skills meet their score threshold',
  protection_policy_reviewed: 'Its protection policy is reviewed and cleared',
  unreviewed_skills_info: 'Skills not yet reviewed (informational)',

  /* Act → Execution Ready */
  at_least_one_executable_skill: 'At least one skill can be executed',
  phase_act: 'Advanced to Act',
  identity_valid: 'Identity valid',
  intent_bound: 'Intent bound',
  governance_materialized: 'Protection materialized',
  green_probe: 'At least one green probe',
  kill_switch_ready: 'Kill switch armed',
  assignment_present: 'Assignment present',
  at_least_one_skill_mapping: 'At least 1 skill mapping is on file',
  at_least_one_test_passed: 'At least 1 PASSED integration test recorded',
  connector_healthy: 'Bound platform connector is healthy',

  /* Not a gate result — the preview itself failed to load. */
  preview_error: 'Gate preview',
};

/**
 * The sentence for a check, never the key itself.
 *
 * A key with no entry means this build is older than the backend answering it. Showing the
 * identifier is how the defect this module replaces looked on screen, so an unknown check is
 * described as what it is — a check whose result is still worth reading, in the message beside
 * it — rather than named.
 */
export function labelForCheck(check) {
  if (!check) return 'Gate check';
  return PHASE_GATE_CHECK_LABELS[check] || 'Gate check';
}

/**
 * The checks a gate reports but does not hold against the agent.
 *
 * <p>Counting these would make a gate that is passing read as "5 of 7 met". The backend's own
 * `allPassed` already ignores them; this is the same fact, needed here because the count is
 * stated separately from the verdict.</p>
 */
export const INFORMATIONAL_CHECKS = new Set([
  'skills_remaining_in_build',
  'failed_evals_warning',
  'unreviewed_skills_info',
]);

/** What the agent is being measured FOR, from the phase it is standing in. */
const NEXT_AFTER = { build: 'Decide', decide: 'Act', act: 'running' };

/**
 * The one standing sentence about whether this agent may move on.
 *
 * <h3>Why it is derived and not written</h3>
 *
 * <p>The verdict is the backend's: {@code allPassed} decides it, and the failing check's own
 * {@code message} says what to do, because that message is written for the person reading it.
 * Nothing here paraphrases either. What this adds is the count — which cannot come from
 * {@code allPassed} — and the phase the agent is being measured for.</p>
 *
 * <p>Returns null when there is nothing true to say: no gate read, no phase, or a preview that
 * failed. A band that says "Not ready" because a read failed is a claim about the agent made
 * from a fact about the network.</p>
 *
 * @param {Object} gate  as {@code readPhaseGate} returns it
 * @param {string} phase the phase the agent is in
 */
export function verdictFromGate(gate, phase) {
  if (!gate || gate.error || !Array.isArray(gate.checks) || !gate.checks.length) return null;
  const next = NEXT_AFTER[String(phase || '').toLowerCase()];
  if (!next) return null;

  const held = gate.checks.filter((c) => !INFORMATIONAL_CHECKS.has(c.id));
  if (!held.length) return null;
  const met = held.filter((c) => c.passed).length;
  const failing = held.find((c) => !c.passed) || null;

  return {
    ready: gate.allPassed === true,
    state: gate.allPassed === true ? `Ready for ${next}` : `Not ready for ${next}`,
    met,
    total: held.length,
    /* The backend's sentence where it wrote one; otherwise what the check is about. A check
       that fails silently is still worth naming. */
    why: failing ? (failing.detail || labelForCheck(failing.id)) : null,
  };
}
